import { AccountActions } from 'affiny-core/modules/account/actions';
import { IProfileUpdateCityBirthdateAttributesContainerProps } from 'affiny-core/modules/account/containers/profileUpdateCityBirthdateAttributesContainerFactory';
import { promisifyDispatch } from 'affiny-core/modules/helpers';
import {
  getAffinyPlaceIdAction,
  getMapsAction,
} from 'affiny-core/modules/maps/actions';
import { ICity } from 'affiny-core/modules/maps/types';
import { postMemberChoicesAction } from 'affiny-core/modules/memberchoices/actions';
import { IProfileJobContainerProps } from 'affiny-core/modules/reflists/containers/profileJobContainerFactory';
import { getAnswersByTextSearch } from 'affiny-core/ui/Question/searchQuestion/helpers';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

type EditMyInformationsConfirmationConfig = IProfileJobContainerProps &
  Omit<
    IProfileUpdateCityBirthdateAttributesContainerProps,
    'isLoading' | 'updateCityAndBirthdate'
  > & {
    onSubmitSucceeded: () => void;
  };

interface IFormValues {
  placeId?: string;
  cities: ICity[];
  place: string;
  job: string;
  jobId?: string;
  birthdate: string;
}

export const useEditMyInformations = ({
  profileJobs,
  defaultJobId,
  attributes,
  city,
  profileJob,
  onSubmitSucceeded,
}: EditMyInformationsConfirmationConfig) => {
  const [jobSearchResults, setJobResults] = useState<
    Array<{ id: string; label: string }>
  >([]);

  const dispatch = useDispatch();

  const onSubmit = useCallback(
    async ({ place, placeId, birthdate, jobId }: IFormValues) => {
      // Update city & birthdate
      const requests = [
        promisifyDispatch(
          dispatch,
          AccountActions.patchCityBirthdate
        )({
          place,
          placeId: placeId ? Number(placeId) : undefined,
          birthdate,
        }),
      ];

      // Update job
      if (jobId) {
        requests.push(
          promisifyDispatch(
            dispatch,
            postMemberChoicesAction
          )({
            profileJob: [Number(jobId)],
          })
        );
      }

      await Promise.all(requests);
      onSubmitSucceeded();
    },
    []
  );

  const formik = useFormik<IFormValues>({
    initialValues: {
      placeId: undefined,
      cities: [],
      place: city || '',
      job: profileJob || '',
      jobId: undefined,
      birthdate:
        attributes && attributes.birthdate
          ? dayjs(attributes.birthdate).format('YYYY-MM-DD')
          : dayjs().format('YYYY-MM-DD'),
    },
    onSubmit,
  });

  const handleCitySearch = useCallback(
    (value: string) => {
      dispatch(
        getMapsAction(value, {
          setFieldValue: formik.setFieldValue,
          setFieldError: formik.setFieldError,
        } as any)
      );
    },
    [formik.setFieldValue, formik.setFieldError]
  );

  const handleCitySelected = useCallback(
    (id?: string) => {
      if (id) {
        const valueCity = formik.values.cities.find(item => item.id === id);

        if (valueCity) {
          dispatch(
            getAffinyPlaceIdAction(valueCity, {
              setFieldValue: formik.setFieldValue,
            } as any)
          );
        }
      } else {
        formik.setFieldValue('place', '');
        formik.setFieldValue('placeId', undefined);
        formik.setFieldValue('cities', []);
      }
    },
    [formik]
  );

  const handleJobSearch = useCallback(
    (value: string) => {
      if (profileJobs) {
        setJobResults(
          getAnswersByTextSearch(profileJobs, value, [], defaultJobId).map(
            item => ({
              id: String(item.id),
              label: item.label,
            })
          )
        );
      }
    },
    [profileJobs]
  );

  const handleJobSelected = useCallback(
    (id?: string) => {
      if (id && profileJobs) {
        const job = profileJobs.find(item => String(item.id) === id);

        if (job) {
          formik.setFieldValue('job', job.label);
          formik.setFieldValue('jobId', id);
        }
      } else {
        formik.setFieldValue('job', '');
        formik.setFieldValue('jobId', undefined);
      }
    },
    [profileJobs, formik]
  );

  const handleDateChanged = useCallback(
    (date: Date) => {
      formik.setFieldValue(
        'birthdate',
        dayjs(date).format('YYYY-MM-DDTHH:mm:ssZZ')
      );
    },
    [formik]
  );

  return {
    birthdate: dayjs(formik.values.birthdate).toDate(),
    hasChanges: formik.dirty,
    formik,
    jobSearchResults,
    handleCitySearch,
    handleCitySelected,
    handleJobSearch,
    handleJobSelected,
    handleDateChanged,
  };
};
